import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Modal, Button, Group, ColorPicker, TextInput, Stack, Box, Text } from '@mantine/core';
import { IconPalette } from '@tabler/icons-react';

// ==================== TYPES ====================

interface BackgroundColorPickerDialogProps {
    /** Current background color value (hex format) */
    background_color?: string;
    /** Callback when color changes */
    onChange: (color: string) => void;
    /** Button label text */
    buttonLabel?: string;
    /** Button size */
    buttonSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /** Disabled state */
    disabled?: boolean;
}

// ==================== CONSTANTS ====================

/** Default background color (white) */
const DEFAULT_COLOR = '#FFFFFF';

/** Regex pattern for validating hex color codes */
const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

/** Button style configuration */
const BUTTON_STYLE = {
    backgroundColor: '#ffffff',
    border: '1px solid #1b1a1aff',
    color: '#000',
} as const;

/** Predefined color swatches */
const COLOR_SWATCHES = [
    '#FFFFFF',
    '#F8F9FA',
    '#E9ECEF',
    '#DEE2E6',
    '#E3F2FD',
    '#FFF3CD',
    '#D4EDDA',
    '#F8D7DA',
    '#FFE8CC',
    '#E7F5FF',
] as const;

// ==================== UTILITY FUNCTIONS ====================

/**
 * Validates if a string is a valid hex color
 */
const isValidHexColor = (value: string): boolean => {
    return value === '' || HEX_COLOR_REGEX.test(value);
};

/**
 * Normalizes color input by ensuring # prefix
 */
const normalizeColorInput = (value: string): string => {
    if (!value) return '';
    return value.startsWith('#') ? value : `#${value}`;
};

/**
 * Calculates perceived brightness of a color (0-255)
 * Uses the YIQ formula for better perception accuracy
 */
const getColorBrightness = (hexColor: string): number => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000;
};

/**
 * Determines if text should be dark or light based on background
 */
const getContrastTextColor = (backgroundColor: string): string => {
    if (!isValidHexColor(backgroundColor)) return '#000000';
    const brightness = getColorBrightness(backgroundColor);
    return brightness > 128 ? '#000000' : '#FFFFFF';
};

// ==================== COMPONENT ====================

/**
 * BackgroundColorPickerDialog Component
 * 
 * A comprehensive color picker dialog for selecting background colors with hex input support.
 * 
 * Features:
 * - Visual color picker with interactive palette
 * - Manual hex code input with real-time validation
 * - Live preview with contrast indication
 * - Remove color option
 * - Keyboard shortcuts (Enter to save, Escape to cancel)
 * - Automatic color normalization
 * - Accessibility support
 * 
 * @component
 * @example
 * ```tsx
 * <BackgroundColorPickerDialog
 *   background_color="#E3F2FD"
 *   onChange={(color) => updateBackgroundColor(color)}
 *   buttonLabel="Choose Color"
 *   buttonSize="sm"
 * />
 * ```
 */
const BackgroundColorPickerDialog: React.FC<BackgroundColorPickerDialogProps> = ({
    background_color,
    onChange,
    buttonLabel = 'Pick Background Color',
    buttonSize = 'xs',
    disabled = false,
}) => {
    // ==================== STATE ====================

    const [opened, setOpened] = useState(false);
    const [color, setColor] = useState(() => background_color || DEFAULT_COLOR);
    const [inputValue, setInputValue] = useState(() => background_color || DEFAULT_COLOR);

    // ==================== EFFECTS ====================

    /**
     * Syncs internal state when external prop changes
     */
    useEffect(() => {
        const newColor = background_color || DEFAULT_COLOR;
        setColor(newColor);
        setInputValue(newColor);
    }, [background_color]);

    /**
     * Resets state when modal closes
     */
    useEffect(() => {
        if (!opened) {
            const resetColor = background_color || DEFAULT_COLOR;
            setColor(resetColor);
            setInputValue(resetColor);
        }
    }, [opened, background_color]);

    // ==================== COMPUTED VALUES ====================

    /** Checks if a valid color is currently selected */
    const hasColor = useMemo(() => {
        return Boolean(color && color !== '' && color !== DEFAULT_COLOR);
    }, [color]);

    /** Validates if the current input is a valid hex code */
    const isValidHex = useMemo(() => {
        return isValidHexColor(inputValue);
    }, [inputValue]);

    /** Determines if save button should be enabled */
    const canSave = useMemo(() => {
        return isValidHex && inputValue !== background_color;
    }, [isValidHex, inputValue, background_color]);

    /** Gets appropriate text color for preview */
    const previewTextColor = useMemo(() => {
        return isValidHex ? getContrastTextColor(color) : '#000000';
    }, [color, isValidHex]);

    // ==================== EVENT HANDLERS ====================

    /** Opens the color picker modal */
    const handleOpen = useCallback(() => {
        setOpened(true);
    }, []);

    /** Closes the color picker modal */
    const handleClose = useCallback(() => {
        setOpened(false);
    }, []);

    /** Handles color changes from the visual picker */
    const handleColorChange = useCallback((newColor: string) => {
        const normalized = normalizeColorInput(newColor);
        setColor(normalized);
        setInputValue(normalized);
    }, []);

    /** Handles manual hex input changes */
    const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = normalizeColorInput(event.currentTarget.value);
        setInputValue(value);

        // Update color picker if valid
        if (isValidHexColor(value)) {
            setColor(value);
        }
    }, []);

    /** Saves the selected color and closes the modal */
    const handleSave = useCallback(() => {
        if (!isValidHex) return;

        onChange(inputValue);
        handleClose();
    }, [inputValue, isValidHex, onChange, handleClose]);

    /** Removes the background color */
    const handleRemove = useCallback(() => {
        setColor(DEFAULT_COLOR);
        setInputValue(DEFAULT_COLOR);
        onChange('');
        handleClose();
    }, [onChange, handleClose]);

    /** Handles keyboard shortcuts */
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' && canSave) {
                event.preventDefault();
                handleSave();
            } else if (event.key === 'Escape') {
                event.preventDefault();
                handleClose();
            }
        },
        [canSave, handleSave, handleClose]
    );

    // ==================== RENDER ====================

    return (
        <>
            {/* Trigger Button */}
            <Button
                size={buttonSize}
                onClick={handleOpen}
                style={BUTTON_STYLE}
                leftSection={<IconPalette size={16} />}
                disabled={disabled}
                aria-label={buttonLabel}
                data-testid="color-picker-trigger"
            >
                {buttonLabel}
            </Button>

            {/* Color Picker Modal */}
            <Modal
                opened={opened}
                onClose={handleClose}
                title="Select Background Color"
                centered
                radius="md"
                size="auto"
                data-testid="color-picker-modal"
                closeOnEscape
                trapFocus
            >
                <Stack gap="md" onKeyDown={handleKeyDown}>
                    {/* Color Picker Component */}
                    <Box data-testid="color-picker-widget">
                        <ColorPicker
                            format="hex"
                            value={color}
                            onChange={handleColorChange}
                            withPicker
                            fullWidth
                            swatches={[...COLOR_SWATCHES]}
                            swatchesPerRow={5}
                        />
                    </Box>

                    {/* Hex Code Input */}
                    <TextInput
                        label="Hex Code"
                        placeholder="#FFFFFF"
                        value={inputValue}
                        onChange={handleInputChange}
                        error={!isValidHex ? 'Invalid hex color format' : null}
                        description="Enter a hex color code (e.g., #FFFFFF or #FFF)"
                        maxLength={9}
                        data-testid="hex-input"
                        styles={{
                            input: {
                                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                                fontSize: '14px',
                            },
                        }}
                    />

                    {/* Color Preview */}
                    {isValidHex && (
                        <Box data-testid="color-preview-container">
                            <Text size="sm" fw={500} mb={8}>
                                Preview
                            </Text>
                            <Box
                                style={{
                                    width: '100%',
                                    height: '80px',
                                    backgroundColor: color,
                                    border: '1px solid #dee2e6',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                                data-testid="color-preview"
                            >
                                <Text
                                    size="sm"
                                    fw={500}
                                    style={{
                                        color: previewTextColor,
                                        textAlign: 'center',
                                        padding: '0 16px',
                                    }}
                                >
                                    Sample Text
                                    <br />
                                    <Text size="xs" opacity={0.8} component="span">
                                        {color.toUpperCase()}
                                    </Text>
                                </Text>
                            </Box>
                        </Box>
                    )}

                    {/* Action Buttons */}
                    <Group justify="space-between" mt="md">
                        <Group>
                            {hasColor && (
                                <Button
                                    variant="outline"
                                    color="red"
                                    onClick={handleRemove}
                                    aria-label="Remove background color"
                                    data-testid="remove-color-button"
                                >
                                    Remove Color
                                </Button>
                            )}
                        </Group>
                        <Group>
                            <Button
                                variant="default"
                                onClick={handleClose}
                                data-testid="cancel-button"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={!canSave}
                                aria-label="Save selected color"
                                data-testid="save-button"
                            >
                                Save
                            </Button>
                        </Group>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
};

export default BackgroundColorPickerDialog;